import { useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type * as echarts from 'echarts';
import {
  formatLocaleNumber,
  getCssVariable,
  getSentimentLabel,
} from '../../../utils/utils';
import useElementDimension from '../../../hooks/useElementDimension';

const chartColor = {
  lineStyleColor: [
    [0.125, getCssVariable('--heatmap-down-5')],
    [0.25, getCssVariable('--heatmap-down-3')],
    [0.375, getCssVariable('--heatmap-down-2')],
    [0.5, getCssVariable('--heatmap-down-1')],
    [0.625, getCssVariable('--heatmap-up-1')],
    [0.75, getCssVariable('--heatmap-up-2')],
    [0.875, getCssVariable('--heatmap-up-3')],
    [1, getCssVariable('--heatmap-up-5')],
  ] as Array<[number, string]>,
  labelColor: getCssVariable('--text-primary'),
  axisTick: getCssVariable('--surface-primary'),
  axisLabel: getCssVariable('--text-secondary'),
  pointerColor: getCssVariable('--chart-senary'),
  anchorColor: {
    color: getCssVariable('--border-tertiary'),
    borderColor: getCssVariable('--accent-primary'),
  },
};

const getGaugeColor = (
  value: number,
  stops: Array<[number, string]>,
): string => {
  const normalized = (value + 1) / 2;
  for (const [stop, color] of stops) {
    if (normalized <= stop) return color;
  }
  return stops[stops.length - 1][1];
};

interface SentimentGaugeChartProps {
  value: number;
}

const GLOBAL_FONT = "'Arial', 'Helvetica', 'Sans-serif'";

const SentimentGaugeChart = ({ value }: SentimentGaugeChartProps) => {
  const label = getSentimentLabel(value.toFixed(2));
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useElementDimension(containerRef);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance().resize();
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const scale = width > 0 ? Math.min(1, Math.max(0.6, width / 360)) : 1;

  const gaugeOption: echarts.EChartsOption = {
    aria: { show: true } as echarts.AriaComponentOption,
    grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
    tooltip: {
      trigger: 'item',
      position: 'top',
      textStyle: {
        fontSize: 12,
        fontFamily: GLOBAL_FONT,
      },
      borderColor: getGaugeColor(value, chartColor.lineStyleColor),
      formatter: (params: any) => {
        const color = getGaugeColor(params.value, chartColor.lineStyleColor);
        return `
          <div>
            <div>${params.seriesName}</div>
            <div style="display: flex; align-items: center; justify-content:space-between; margin-top:4px;">
              <div style="display: flex; align-items: center; gap: 4px; margin-right: 8px;">
                <span style="margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>
                <span>${params.name}</span>
              </div>
              <strong>${formatLocaleNumber(params.value, 2)}</strong>
            </div>
          </div>
        `;
      },
    },
    series: [
      {
        name: 'Bull Bear Sentiment',
        type: 'gauge',
        emphasis: { disabled: true },
        title: {
          show: true,
          offsetCenter: [0, 25 * scale],
          fontStyle: 'normal',
          fontWeight: 'bold',
          fontSize: Math.round(16 * scale),
          fontFamily: GLOBAL_FONT,
          color: chartColor.labelColor,
        },
        detail: { show: false },
        radius: '100%',
        data: [{ value, name: label }],
        startAngle: 180,
        endAngle: 0,
        clockwise: true,
        min: -1,
        max: 1,
        splitNumber: 8,
        axisLine: {
          show: true,
          lineStyle: {
            width: Math.round(40 * scale),
            color: chartColor.lineStyleColor,
          },
        },
        splitLine: { show: false },
        axisTick: {
          show: true,
          splitNumber: 1,
          length: Math.round(60 * scale),
          distance: Math.round(-45 * scale),
          lineStyle: {
            type: 'solid',
            color: chartColor.axisTick,
            width: 1,
          },
        },
        axisLabel: {
          show: true,
          distance: Math.round(-55 * scale),
          fontSize: Math.round(12 * scale),
          fontFamily: GLOBAL_FONT,
          color: chartColor.axisLabel,
          formatter: (v: number) => String(formatLocaleNumber(v, 2)),
        },
        center: ['50%', '75%'],
        pointer: {
          show: true,
          showAbove: true,
          length: '65%',
          width: 5,
          keepAspect: false,
          itemStyle: { color: chartColor.pointerColor },
          offsetCenter: [0, '-3%'],
        },
        anchor: {
          show: true,
          showAbove: true,
          size: 12,
          icon: 'circle',
          itemStyle: {
            color: chartColor.anchorColor.color,
            borderColor: chartColor.anchorColor.borderColor,
            borderWidth: 4,
          },
        },
      },
      {
        type: 'gauge',
        radius: '130%',
        center: ['50%', '75%'],
        startAngle: 180,
        endAngle: 0,
        min: -1,
        max: 1,
        silent: true,
        axisLine: { show: false },
        splitLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        pointer: { show: false },
        detail: { show: false },
        title: { show: false },
        data: [{ value }],
        anchor: {
          show: true,
          showAbove: false,
          size: 16,
          icon: 'circle',
          itemStyle: {
            color: chartColor.anchorColor.color,
            borderColor: chartColor.anchorColor.borderColor,
            borderWidth: 1,
          },
        },
      },
    ],
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <ReactECharts
        ref={chartRef}
        option={gaugeOption}
        style={{ width: '100%', height: '200px' }}
      />
    </div>
  );
};

export default SentimentGaugeChart;
